import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useJoinCourse } from "@/lib/useJoinCourse";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CreditCard, Calendar, ShieldCheck, XCircle } from "lucide-react";

export default function YogaBilling() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [sub, setSub] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/yoga/billing"); return; }
    api.get("/yoga/subscription").then(({ data }) => setSub(data.subscription)).catch(() => {});
  }, [user, loading, nav]);

  const { join, joining } = useJoinCourse();

  const cancel = async () => {
    if (!window.confirm("Stop future renewals? You keep access until the date above.")) return;
    try {
      await api.post("/yoga/cancel");
      toast.success("Membership cancelled. You'll keep access until it expires.");
      const { data } = await api.get("/yoga/subscription");
      setSub(data.subscription);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };

  return (
    <main className="max-w-3xl mx-auto px-6 lg:px-12 py-12">
      <div className="overline mb-2">Membership</div>
      <h1 className="font-display text-4xl lg:text-5xl mb-8">Manage Your Membership</h1>

      <Card className="border-border mb-6">
        <CardContent className="p-6 lg:p-8">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="text-primary" size={22}/>
            <div className="font-display text-2xl">
              {sub?.status === "active" ? "Active" : sub?.status === "cancelled" ? "Cancelled (still active until expiry)" : "Not active"}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-widest mb-1">Plan</div>
              <div className="font-medium">₹100 for 21 days, then ₹251/month</div>
              {sub?.mode === "one_time" && (
                <div className="text-[11px] text-secondary mt-1">Manual renewal — auto-debit is not switched on yet</div>
              )}
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-widest mb-1">Renews / expires</div>
              <div className="font-medium flex items-center gap-1"><Calendar size={12}/>{sub?.expires_at ? new Date(sub.expires_at).toLocaleDateString() : "—"}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="overline mb-2">Extend / Renew</div>
            <p className="text-sm text-muted-foreground mb-4">
              Renewals are collected automatically at ₹251/month. If your access lapsed, you can restart here.
            </p>
            <Button onClick={join} disabled={joining} className="w-full btn-pill bg-primary text-primary-foreground" data-testid="yoga-renew-btn">
              <CreditCard size={14} className="mr-1.5"/> {joining ? "Opening…" : "Restart membership"}
            </Button>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="overline mb-2">Cancel</div>
            <p className="text-sm text-muted-foreground mb-4">Stop future charges. Access continues until the expiry date above.</p>
            <Button onClick={cancel} variant="outline" className="w-full btn-pill border-destructive/40 text-destructive hover:bg-destructive/10" data-testid="yoga-cancel-btn">
              <XCircle size={14} className="mr-1.5"/> Cancel Membership
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="border border-dashed border-secondary/40 rounded-xl p-4 bg-secondary/5 text-xs text-secondary">
        <b>How billing works:</b> ₹100 covers your first 21 days. Razorpay then collects ₹251 every month
        automatically on the mandate you approved, at the same rate for every renewal. Cancelling stops
        future charges and keeps your access until the date above.
      </div>

      <div className="mt-8 text-center">
        <Link to="/yoga/dashboard" className="text-sm text-primary underline">← Back to dashboard</Link>
      </div>
    </main>
  );
}
